import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CATEGORIES_PREVIEW = [
  { icon: '💻', label: 'Tech',      color: 'bg-indigo-500' },
  { icon: '📈', label: 'Finance',   color: 'bg-emerald-500' },
  { icon: '👗', label: 'Fashion',   color: 'bg-pink-500' },
  { icon: '⚽', label: 'Sports',    color: 'bg-orange-500' },
  { icon: '🏃', label: 'Health',    color: 'bg-teal-500' },
  { icon: '📚', label: 'Education', color: 'bg-blue-500' },
  { icon: '🚀', label: 'Space',     color: 'bg-violet-500' },
  { icon: '🔬', label: 'Science',   color: 'bg-purple-500' },
];

function IllustratedPanel() {
  return (
    <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-purple-700 via-indigo-800 to-slate-900 p-12 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}
      />
      <div className="absolute top-20 right-4  w-48 h-48 rounded-full bg-purple-400/10 blur-3xl" />
      <div className="absolute bottom-16 left-6 w-64 h-64 rounded-full bg-indigo-400/10 blur-3xl" />

      <div className="relative z-10">
        <div className="text-white font-bold text-3xl tracking-tight mb-1">TERAINFO</div>
        <p className="text-purple-200 text-sm">8 categories. One platform.</p>
      </div>

      {/* Category pills preview */}
      <div className="relative z-10 flex-1 flex items-center justify-center py-12">
        <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
          {CATEGORIES_PREVIEW.map(({ icon, label, color }) => (
            <div
              key={label}
              className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-3"
            >
              <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center text-sm shadow`}>
                {icon}
              </div>
              <span className="text-white text-sm font-medium">{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 space-y-3">
        {[
          { icon: '🆓', text: 'Completely free to join' },
          { icon: '📡', text: 'Real articles, live from the web' },
          { icon: '💬', text: 'Ask questions, get admin answers' },
        ].map(({ icon, text }) => (
          <div key={text} className="flex items-center gap-3">
            <span className="text-lg leading-none">{icon}</span>
            <span className="text-purple-100 text-sm">{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RegisterPage() {
  const { register }          = useAuth();
  const navigate              = useNavigate();
  const [form, setForm]       = useState({ name: '', email: '', password: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message ?? 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <IllustratedPanel />

      {/* Right: form */}
      <div className="flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-600 rounded-2xl mb-3 shadow-lg">
              <span className="text-2xl">📰</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">TERAINFO</h1>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-1">Create your account</h2>
          <p className="text-gray-500 text-sm mb-7">Free forever — no credit card needed</p>

          {error && (
            <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 mb-5 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">Full name</label>
              <input
                id="name" type="text" name="name"
                value={form.name} onChange={handleChange}
                required autoComplete="name" placeholder="Jane Doe"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition bg-white"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
              <input
                id="email" type="email" name="email"
                value={form.email} onChange={handleChange}
                required autoComplete="email" placeholder="you@example.com"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition bg-white"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input
                id="password" type="password" name="password"
                value={form.password} onChange={handleChange}
                required minLength={8} autoComplete="new-password" placeholder="Minimum 8 characters"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition bg-white"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {loading ? 'Creating account…' : 'Create Free Account'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-7">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-600 hover:text-indigo-800 font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
